import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import DataRow from './DataRow.jsx'; // Corrected import path

const PromptDialog = forwardRef(({ initialLabelsData = [], onFinish, onClose, onBeforePrint }, ref) => {
  const [allLabelsData, setAllLabelsData] = useState([]);
  const [currentLabelIndex, setCurrentLabelIndex] = useState(0);
  const [useForAll, setUseForAll] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [errors, setErrors] = useState({});
  const tableRef = useRef(null);
  const [columnWidths, setColumnWidths] = useState({});
  const isResizing = useRef(null);
  const [isResizingActive, setIsResizingActive] = useState(false);
  const columnsOrder = ['name', 'value', 'preview', 'labelNumber', 'pageNum'];

  const validate = (item, value) => {
    const itemName = item.name || 'Unknown';
    if (!item.allowEmpty && !value) {
      return 'This field cannot be empty.';
    }
    const limit = Number(item.limitChars);
    if (Number.isFinite(limit) && limit > 0 && (value || '').length > limit) {
      return `Character limit of ${limit} exceeded.`;
    }
    if (item.regex && value) {
      try {
        let regexSource = item.regex.trim();
        let flags = '';

        // Safely extract pattern and flags if format is /pattern/flags
        const slashMatch = regexSource.match(/^\/(.*)\/([gimsuy]*)$/);
        if (slashMatch) {
          regexSource = slashMatch[1];
          flags = slashMatch[2];
        }

        const reg = new RegExp(regexSource, flags);
        if (!reg.test(value)) {
          return `Invalid format`;
        }
      } catch (e) {
        console.error('[DEBUG] Invalid regex pattern crash:', item.regex, e);
      }
    }
    return '';
  };

  useEffect(() => {
    if (tableRef.current) {
      const ths = tableRef.current.querySelectorAll('thead th');
      if (ths.length > 0 && Object.keys(columnWidths).length === 0) {
        const widths = {};
        ths.forEach(th => {
          widths[th.dataset.column] = th.offsetWidth;
        });
        setColumnWidths(widths);
      }
    }
  }, [tableRef.current, allLabelsData]); // Rerun when data loads

  useEffect(() => {
    if (initialLabelsData && initialLabelsData.length > 0) {
      const processedData = initialLabelsData.map(label => ({
        ...label,
        data: label.data.map(item => ({
          ...item,
          preview: item.value,
        })),
      }));
      setAllLabelsData(processedData);

      const initialErrors = {};
      processedData.forEach((label, labelIndex) => {
        label.data.forEach((item, itemIndex) => {
          initialErrors[`${labelIndex}-${itemIndex}`] = validate(item, item.value);
        });
      });
      setErrors(initialErrors);
    }
  }, [initialLabelsData]);

  const handleMouseDown = (e, columnIndex) => {
    setIsResizingActive(true);
    const column = columnsOrder[columnIndex];
    const nextColumn = columnsOrder[columnIndex + 1];

    isResizing.current = {
      column,
      nextColumn,
      startX: e.clientX,
      startWidth: columnWidths[column],
      nextStartWidth: nextColumn ? columnWidths[nextColumn] : 0,
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!isResizing.current) return;
    const { column, nextColumn, startX, startWidth, nextStartWidth } = isResizing.current;
    const deltaX = e.clientX - startX;

    const newWidth = startWidth + deltaX;
    const newNextWidth = nextStartWidth - deltaX;

    if (newWidth > 50 && (!nextColumn || newNextWidth > 50)) {
      setColumnWidths(prev => {
        const newWidths = { ...prev, [column]: newWidth };
        if (nextColumn) {
          newWidths[nextColumn] = newNextWidth;
        }
        return newWidths;
      });
    }
  };

  const handleMouseUp = () => {
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
    isResizing.current = null;
    setIsResizingActive(false);
  };


  useImperativeHandle(ref, () => ({
    close() {
      handleClose();
    }
  }));

  const handleValueChange = (itemIndex, newValue) => {
    const labelIdx = currentLabelIndex;

    // Never reject the keystroke here. An over-limit value is reported by validate()
    // and blocked by isNextDisabled, the same way allowEmpty and regex already work.
    // Rejecting it made an already-too-long value impossible to shorten.
    const error = validate(allLabelsData[labelIdx].data[itemIndex], newValue);

    setErrors(prevErrors => ({
      ...prevErrors,
      [`${labelIdx}-${itemIndex}`]: error,
    }));

    setAllLabelsData(prevData => {
      const updatedData = [...prevData];
      const label = { ...updatedData[labelIdx] };
      const items = [...label.data];

      items[itemIndex] = { ...items[itemIndex], value: newValue, preview: newValue };
      label.data = items;
      updatedData[labelIdx] = label;

      return updatedData;
    });
  };

  const handleNext = () => {
    const currentLabelErrors = allLabelsData[currentLabelIndex].data.some((item, index) => {
      const key = `${currentLabelIndex}-${index}`;
      return errors[key];
    });

    if (currentLabelErrors) {
      return;
    }

    const isLastLabel = currentLabelIndex === allLabelsData.length - 1;

    let updatedData = JSON.parse(JSON.stringify(allLabelsData));
    let updatedErrors = { ...errors };

    if (useForAll) {
      const currentValues = new Map();
      updatedData[currentLabelIndex].data.forEach(item => {
        // Include cleared fields too, so an intentionally emptied value carries
        // forward instead of leaving the old value on the remaining labels.
        currentValues.set(item.name, item.value ?? '');
      });

      for (let i = currentLabelIndex + 1; i < updatedData.length; i++) {
        updatedData[i].data = updatedData[i].data.map((futureItem, itemIndex) => {
          if (currentValues.has(futureItem.name)) {
            const newValue = currentValues.get(futureItem.name);
            const error = validate(futureItem, newValue);
            updatedErrors[`${i}-${itemIndex}`] = error;
            return {
              ...futureItem,
              value: newValue,
              preview: newValue,
            };
          }
          return futureItem;
        });
      }
    }

    setAllLabelsData(updatedData);
    setErrors(updatedErrors);

    if (isLastLabel) {
      if (onBeforePrint && typeof onBeforePrint === 'function') {
        onBeforePrint(updatedData);
      }
      if (onFinish && typeof onFinish === 'function') {
        onFinish(updatedData);
      }
      handleClose();
    } else {
      setCurrentLabelIndex(currentLabelIndex + 1);
    }
  };

  const handleBack = () => {
    if (currentLabelIndex > 0) {
      setCurrentLabelIndex(currentLabelIndex - 1);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  }

  const handleCancel = () => {
    console.log('Cancel Print Job clicked.');
    handleClose();
  };

  if (!allLabelsData || allLabelsData.length === 0 || !isVisible) {
    return null;
  }

  const currentLabel = allLabelsData[currentLabelIndex];
  const isNextDisabled = currentLabel.data.some((item, index) => !!errors[`${currentLabelIndex}-${index}`]);

  return (
    <div className={`prompt-dialog-overlay ${isResizingActive ? 'is-resizing' : ''}`}>
      <div className="prompt-dialog">
        <div className="prompt-dialog-header">
          <h2 className="prompt-dialog-title">Enter Prompt Data</h2>
          <p className="prompt-dialog-subtitle">{currentLabel.labelName}</p>
        </div>
        <div className="prompt-dialog-body">
          <div className="prompt-dialog-table-container">
            <table className="prompt-dialog-table" ref={tableRef}>
              <colgroup>
                <col style={{ width: columnWidths.name ? `${columnWidths.name}px` : '20%' }} />
                <col style={{ width: columnWidths.value ? `${columnWidths.value}px` : '30%' }} />
                <col style={{ width: columnWidths.preview ? `${columnWidths.preview}px` : '20%' }} />
                <col style={{ width: columnWidths.labelNumber ? `${columnWidths.labelNumber}px` : '15%' }} />
                <col style={{ width: columnWidths.pageNum ? `${columnWidths.pageNum}px` : '15%' }} />
              </colgroup>
              <thead className="prompt-dialog-table-header">
                <tr>
                  <th className="prompt-dialog-table-header-cell" data-column="name">
                    Name
                    <div className="resizer" onMouseDown={(e) => handleMouseDown(e, 0)}></div>
                  </th>
                  <th className="prompt-dialog-table-header-cell" data-column="value">
                    Value
                    <div className="resizer" onMouseDown={(e) => handleMouseDown(e, 1)}></div>
                  </th>
                  <th className="prompt-dialog-table-header-cell" data-column="preview">
                    Data Preview
                    <div className="resizer" onMouseDown={(e) => handleMouseDown(e, 2)}></div>
                  </th>
                  <th className="prompt-dialog-table-header-cell" data-column="labelNumber">
                    Label #
                    <div className="resizer" onMouseDown={(e) => handleMouseDown(e, 3)}></div>
                  </th>
                  <th className="prompt-dialog-table-header-cell" data-column="pageNum">
                    Page #
                  </th>
                </tr>
              </thead>
              <tbody className="prompt-dialog-table-body">
                {currentLabel.data.map((item, index) => (
                  <DataRow
                    key={item.id}
                    item={item}
                    onValueChange={(newValue) => handleValueChange(index, newValue)}
                    error={errors[`${currentLabelIndex}-${index}`]}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="prompt-dialog-checkbox-container">
            <input
              type="checkbox"
              id="useValues"
              checked={useForAll}
              onChange={() => setUseForAll(!useForAll)}
              className="prompt-dialog-checkbox"
            />
            <label htmlFor="useValues" className="prompt-dialog-checkbox-label">
              Use these values for the rest of print job
            </label>
          </div>
        </div>
        <div className="prompt-dialog-footer">
          <div>
            {currentLabelIndex > 0 && (
              <button onClick={handleBack} className="prompt-dialog-button prompt-dialog-back-button">
                Back
              </button>
            )}
          </div>
          <div className="prompt-dialog-button-group">
            <button onClick={handleCancel} className="prompt-dialog-button prompt-dialog-cancel-button">
              Cancel Print Job
            </button>
            <button onClick={handleNext} className="prompt-dialog-button prompt-dialog-next-button" disabled={isNextDisabled}>
              {currentLabelIndex === allLabelsData.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PromptDialog;